import type MiniSearch from "minisearch";
import { create } from "zustand";
import type { EnrichedRestaurant, SearchIndexDoc } from "../../src/types";
import {
  loadOrigin,
  loadStatusMap,
  type StatusMap,
  saveOrigin,
  saveStatusMap,
} from "./persistence";
import type { ExplorerRestaurant, GeoStatus, Layout, SortKey, Status, Tab } from "./types-explorer";

interface StoreState {
  // data
  restaurants: ExplorerRestaurant[];
  loadedMenus: Record<string, EnrichedRestaurant>;
  searchIndex: MiniSearch<SearchIndexDoc> | null;
  searchIndexLoading: boolean;

  // prefetch progress (transient; not persisted)
  prefetchTotal: number;
  prefetchSettled: number;

  // ui state
  tab: Tab;
  layout: Layout;
  expandedSlugs: string[];
  searchQuery: string;
  parkFilter: string[];
  serviceFilter: string[];
  priceFilter: string[];
  hideNoGF: boolean;
  showUnknown: boolean;
  sortBy: SortKey;
  originId: string;
  previousOriginId: string;
  geoCoords: { lat: number; lng: number } | null;
  geoStatus: GeoStatus;
  geoError: string | null;

  // persisted
  statusMap: StatusMap;

  // actions
  setRestaurants(rs: ExplorerRestaurant[]): void;
  setSearchIndex(idx: MiniSearch<SearchIndexDoc>): void;
  setSearchIndexLoading(b: boolean): void;
  storeMenu(slug: string, m: EnrichedRestaurant): void;
  incrementPrefetchTotal(n: number): void;
  incrementPrefetchSettled(n: number): void;

  setTab(t: Tab): void;
  setLayout(l: Layout): void;
  toggleExpanded(slug: string): void;
  collapseAll(): void;
  setSearchQuery(q: string): void;
  togglePark(p: string): void;
  toggleService(s: string): void;
  togglePrice(p: string): void;
  setHideNoGF(v: boolean): void;
  setShowUnknown(v: boolean): void;
  setSortBy(s: SortKey): void;
  setOriginId(id: string): void;
  requestGeo(maxAgeMs?: number): Promise<void>;
  clearGeoError(): void;

  setStatus(slug: string, status: Status | null): void;
  clearAllVetoed(): void;

  resetForTest(): void;
}

const toggleIn = (arr: string[], v: string): string[] =>
  arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

const persistedOrigin = loadOrigin();

export const useStore = create<StoreState>((set, get) => ({
  restaurants: [],
  loadedMenus: {},
  searchIndex: null,
  searchIndexLoading: false,
  prefetchTotal: 0,
  prefetchSettled: 0,

  tab: "explore",
  layout: "cards",
  expandedSlugs: [],
  searchQuery: "",
  parkFilter: [],
  serviceFilter: [],
  priceFilter: [],
  hideNoGF: true,
  showUnknown: true,
  sortBy: "safe",
  originId: persistedOrigin.originId,
  previousOriginId: persistedOrigin.previousOriginId,
  geoCoords: persistedOrigin.geoCoords,
  geoStatus: "idle",
  geoError: null,

  statusMap: loadStatusMap(),

  setRestaurants: (rs) => set({ restaurants: rs }),
  setSearchIndex: (idx) => set({ searchIndex: idx, searchIndexLoading: false }),
  setSearchIndexLoading: (b) => set({ searchIndexLoading: b }),
  storeMenu: (slug, m) => set((s) => ({ loadedMenus: { ...s.loadedMenus, [slug]: m } })),
  incrementPrefetchTotal: (n) => set((s) => ({ prefetchTotal: s.prefetchTotal + n })),
  incrementPrefetchSettled: (n) => set((s) => ({ prefetchSettled: s.prefetchSettled + n })),

  setTab: (t) => set({ tab: t, expandedSlugs: [] }),
  setLayout: (l) => set({ layout: l }),
  toggleExpanded: (slug) => set((s) => ({ expandedSlugs: toggleIn(s.expandedSlugs, slug) })),
  collapseAll: () => set({ expandedSlugs: [] }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  togglePark: (p) => set((s) => ({ parkFilter: toggleIn(s.parkFilter, p) })),
  toggleService: (v) => set((s) => ({ serviceFilter: toggleIn(s.serviceFilter, v) })),
  togglePrice: (v) => set((s) => ({ priceFilter: toggleIn(s.priceFilter, v) })),
  setHideNoGF: (v) => set({ hideNoGF: v }),
  setShowUnknown: (v) => set({ showUnknown: v }),
  setSortBy: (s) => set({ sortBy: s }),

  setOriginId: (id) => {
    if (id === "geo") {
      // Persist immediately so a reload mid-acquiring resumes the geo selection
      // rather than reverting to the previous origin (see spec "Reload behavior").
      set({ originId: "geo" });
      saveOrigin({
        originId: "geo",
        previousOriginId: get().previousOriginId,
        geoCoords: get().geoCoords,
      });
      void get().requestGeo();
      return;
    }
    set({ originId: id, previousOriginId: id });
    saveOrigin({ originId: id, previousOriginId: id, geoCoords: get().geoCoords });
  },

  requestGeo: async (maxAgeMs = 60_000) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      set({ geoStatus: "unavailable" });
      return;
    }
    set({ geoStatus: "acquiring", geoError: null });
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          maximumAge: maxAgeMs,
          timeout: 15_000,
        });
      });
      const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      set({ geoStatus: "ready", geoCoords: coords });
      saveOrigin({
        originId: "geo",
        previousOriginId: get().previousOriginId,
        geoCoords: coords,
      });
    } catch (e) {
      const err = e as GeolocationPositionError;
      if (err && typeof err.code === "number" && err.code === err.PERMISSION_DENIED) {
        set({ geoStatus: "denied" });
      } else if (err && typeof err.code === "number" && err.code === err.POSITION_UNAVAILABLE) {
        set({ geoStatus: "unavailable" });
      } else {
        set({ geoStatus: "error", geoError: err?.message || "unknown error" });
      }
      const prev = get().previousOriginId;
      set({ originId: prev });
      saveOrigin({ originId: prev, previousOriginId: prev, geoCoords: null });
    }
  },

  clearGeoError: () => set({ geoStatus: "idle", geoError: null }),

  setStatus: (slug, status) =>
    set((s) => {
      const next = { ...s.statusMap };
      if (status === null) delete next[slug];
      else next[slug] = status;
      saveStatusMap(next);
      return { statusMap: next };
    }),

  clearAllVetoed: () =>
    set((s) => {
      const next = Object.fromEntries(
        Object.entries(s.statusMap).filter(([, v]) => v !== "vetoed")
      );
      saveStatusMap(next);
      return { statusMap: next };
    }),

  resetForTest: () =>
    set({
      restaurants: [],
      loadedMenus: {},
      searchIndex: null,
      searchIndexLoading: false,
      prefetchTotal: 0,
      prefetchSettled: 0,
      tab: "explore",
      layout: "cards",
      expandedSlugs: [],
      searchQuery: "",
      parkFilter: [],
      serviceFilter: [],
      priceFilter: [],
      hideNoGF: true,
      showUnknown: true,
      sortBy: "safe",
      originId: "loc_mk",
      previousOriginId: "loc_mk",
      geoCoords: null,
      geoStatus: "idle",
      geoError: null,
      statusMap: {},
    }),
}));
